import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import CardContainer from "@/components/CardContainer";
import { QrCode } from "@/components/qrcode";
import { useAuthStore } from "@/stores/useAuth";
import { GET_ONE_LINK_DEEPLINK_URL } from "@/codegen/graphql/queries/GetOneLinkDeeplinkUrl.query";

// Plain OneLink fallback — kept in sync with DownloadAppBanner.tsx.
const ONELINK_URL = "https://shortsapp.onelink.me/RpbD/0hxetmhb";

export const Route = createFileRoute("/_external/get-app")({
  component: RouteComponent,
  // The QR encodes a per-user authenticated OneLink, so it's only meaningful
  // for a real logged-in account — anonymous device users (which also hold an
  // access token) are bounced home.
  beforeLoad: () => {
    const { currentUser } = useAuthStore.getState();
    if (!currentUser?.id || currentUser.providers.length === 0) {
      throw redirect({ to: "/" });
    }
  },
});

// Desktop lands here from the "Get the App" button: the tokenized OneLink is
// minted on this authenticated session and shown as a QR, so the phone that
// scans it auto-authenticates without fetching anything itself.
function RouteComponent() {
  const { t } = useTranslation();
  const { data, loading } = useQuery(GET_ONE_LINK_DEEPLINK_URL, {
    fetchPolicy: "network-only",
  });

  return (
    <div className="mx-auto flex min-h-[60vh] flex-col items-center justify-center px-4">
      <CardContainer className="flex w-full max-w-sm flex-col items-center gap-6 rounded-xl p-8 text-center">
        <img
          src="/SugarBaby.webp"
          alt={t("get_app_logo_alt")}
          className="size-20 rounded-lg"
        />
        <div className="space-y-2">
          <h1 className="text-xl font-bold">{t("get_app_title")}</h1>
          <p className="text-sm text-neutral-400">{t("get_app_qr_message")}</p>
        </div>
        <QrCode
          url={data?.oneLinkDeeplinkUrl ?? ONELINK_URL}
          loading={loading}
          size={480}
          className="size-60 overflow-hidden rounded-sm"
          data-test-id="download-app-qr"
        />
      </CardContainer>
    </div>
  );
}
